import { useEffect, useMemo, useState } from 'react';
import {
  Drawer, Box, Typography, TextField, Button, Stack, MenuItem, Divider,
  Grid, FormControlLabel, Switch, Alert, IconButton, LinearProgress, InputAdornment,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import {
  TENANT_PLAN_FORM_TABS,
  TENANT_PLAN_MAX_USERS_MAX,
  TENANT_PLAN_MAX_USERS_MIN,
  TENANT_PLAN_PRICE_MAX,
  TENANT_PLAN_PRICE_MIN,
  TENANT_PLAN_TRIAL_DAYS_MAX,
  TENANT_PLAN_TRIAL_DAYS_MIN,
} from '../../constants/tenantSubscriptionPlanConfig';
import {
  BILLING_INTERVAL_OPTIONS,
  PLAN_CURRENCY_OPTIONS,
  emptyPlanForm,
  planRowToForm,
  validatePlanForm,
  buildPlanPayload,
  slugifyPlanCode,
  validatePlanDescription,
  validatePlanHighlights,
  isDuplicatePlanCode,
  type TenantSubscriptionPlanPayload,
  type TenantSubscriptionPlanRow,
} from '../../services/tenantSubscriptionPlanService';

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  plan?: TenantSubscriptionPlanRow | null;
  existingCodes?: string[];
  loading?: boolean;
  error?: string;
  onClose: () => void;
  onSubmit: (payload: TenantSubscriptionPlanPayload) => void;
}

const TAB_IDS = TENANT_PLAN_FORM_TABS.map((t) => t.id);

export default function TenantSubscriptionPlanFormDrawer({
  open, mode, plan, existingCodes = [], loading, error, onClose, onSubmit,
}: Props) {
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState<TenantSubscriptionPlanPayload>(emptyPlanForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [autoCode, setAutoCode] = useState(true);
  const [highlightsText, setHighlightsText] = useState('');

  const validationOpts = useMemo(() => ({
    mode,
    existingCodes,
    excludeCode: mode === 'edit' ? plan?.code : undefined,
  }), [mode, existingCodes, plan?.code]);

  useEffect(() => {
    if (!open) return;
    setTab(0);
    setErrors({});
    setAutoCode(mode === 'create');
    const next = plan && mode === 'edit' ? planRowToForm(plan) : emptyPlanForm();
    setForm(next);
    setHighlightsText((next.features || []).join('\n'));
  }, [open, mode, plan]);

  useEffect(() => {
    if (mode !== 'create' || !autoCode) return;
    const next = slugifyPlanCode(form.name);
    setForm((f) => (f.code === next ? f : { ...f, code: next }));
  }, [form.name, autoCode, mode]);

  useEffect(() => {
    if (!open || mode !== 'create' || !form.code.trim()) return;
    if (!isDuplicatePlanCode(form.code, existingCodes)) {
      setErrors((prev) => {
        if (prev.code !== 'This plan code is already used') return prev;
        const next = { ...prev };
        delete next.code;
        return next;
      });
      return;
    }
    setErrors((prev) => ({ ...prev, code: 'This plan code is already used' }));
  }, [form.code, existingCodes, mode, open]);

  useEffect(() => {
    if (!open || !error) return;
    const lower = error.toLowerCase();
    if (lower.includes('already exists') || lower.includes('already used') || lower.includes('duplicate')) {
      setTab(0);
      setErrors((prev) => ({ ...prev, code: 'This plan code is already used' }));
    }
  }, [error, open]);

  const patch = (p: Partial<TenantSubscriptionPlanPayload>) => setForm((f) => ({ ...f, ...p }));
  const activeTabId = TAB_IDS[tab];
  const tabMeta = TENANT_PLAN_FORM_TABS[tab];
  const canSave = useMemo(
    () => Object.keys(validatePlanForm(form, undefined, validationOpts)).length === 0,
    [form, validationOpts],
  );

  const goNext = () => {
    const tabErrors = validatePlanForm(form, activeTabId, validationOpts);
    if (Object.keys(tabErrors).length) {
      setErrors(tabErrors);
      return;
    }
    setErrors({});
    setTab((t) => Math.min(t + 1, TAB_IDS.length - 1));
  };

  const handleSave = () => {
    for (let i = 0; i < TAB_IDS.length; i += 1) {
      const tabErrors = validatePlanForm(form, TAB_IDS[i], validationOpts);
      if (Object.keys(tabErrors).length) {
        setErrors(tabErrors);
        setTab(i);
        return;
      }
    }
    onSubmit(buildPlanPayload(form));
  };

  const progress = ((tab + 1) / TAB_IDS.length) * 100;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 480, md: 520 } } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
          <Box>
            <Typography variant="h6" fontWeight={800}>
              {mode === 'create' ? 'New subscription plan' : 'Edit subscription plan'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Step {tab + 1} of {TAB_IDS.length} · {tabMeta.subtitle}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}><Close /></IconButton>
        </Stack>

        <LinearProgress variant="determinate" value={progress} sx={{ height: 3 }} />

        <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
          {error && !errors.code && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {activeTabId === 'info' && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  required
                  label="Plan name"
                  value={form.name}
                  error={!!errors.name}
                  helperText={errors.name || 'Required — e.g. Free, Starter, Professional'}
                  onChange={(e) => {
                    const name = e.target.value;
                    patch({ name });
                    setErrors((prev) => {
                      const next = { ...prev };
                      if (!name.trim()) next.name = 'Plan name is required';
                      else if (name.trim().length < 2) next.name = 'Plan name must be at least 2 characters';
                      else delete next.name;
                      return next;
                    });
                  }}
                  autoFocus={mode === 'create'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  required
                  label="Plan code"
                  value={form.code}
                  disabled={mode === 'edit'}
                  error={!!errors.code}
                  helperText={
                    errors.code
                    || (mode === 'edit'
                      ? 'Code cannot be changed after create'
                      : (autoCode
                        ? 'Auto-generated from plan name'
                        : 'Unique id, e.g. starter'))
                  }
                  onChange={(e) => {
                    setAutoCode(false);
                    patch({ code: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') });
                  }}
                  InputProps={mode === 'create' ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <FormControlLabel
                          sx={{ mr: 0, ml: 0.5 }}
                          control={(
                            <Switch
                              size="small"
                              checked={autoCode}
                              onChange={(e) => {
                                const on = e.target.checked;
                                setAutoCode(on);
                                if (on) patch({ code: slugifyPlanCode(form.name) });
                              }}
                            />
                          )}
                          label={<Typography variant="caption" fontWeight={700}>Auto</Typography>}
                        />
                      </InputAdornment>
                    ),
                  } : undefined}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  label="Description"
                  value={form.description || ''}
                  placeholder="What this plan is for — shown when creating a tenant"
                  error={!!errors.description}
                  helperText={errors.description}
                  onChange={(e) => {
                    const description = e.target.value;
                    patch({ description });
                    const descriptionError = validatePlanDescription(description);
                    setErrors((prev) => {
                      if (descriptionError) return { ...prev, description: descriptionError };
                      if (!prev.description) return prev;
                      const next = { ...prev };
                      delete next.description;
                      return next;
                    });
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" type="number" label="Sort order" value={form.sortOrder}
                  helperText="Lower numbers appear first in lists"
                  onChange={(e) => patch({ sortOrder: Number(e.target.value) })} />
              </Grid>
            </Grid>
          )}

          {activeTabId === 'pricing' && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Price"
                  value={form.price}
                  error={!!errors.price}
                  helperText={
                    errors.price
                    || `0 for custom / enterprise · max ${TENANT_PLAN_PRICE_MAX.toLocaleString('en-IN')}`
                  }
                  inputProps={{
                    min: TENANT_PLAN_PRICE_MIN,
                    max: TENANT_PLAN_PRICE_MAX,
                    step: 1,
                  }}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const next = raw === '' ? 0 : Number(raw);
                    const price = Number.isFinite(next)
                      ? Math.min(Math.max(next, TENANT_PLAN_PRICE_MIN), TENANT_PLAN_PRICE_MAX)
                      : TENANT_PLAN_PRICE_MIN;
                    patch({ price });
                    setErrors((prev) => {
                      if (!prev.price) return prev;
                      const nextErrors = { ...prev };
                      delete nextErrors.price;
                      return nextErrors;
                    });
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" select label="Currency" value={form.currency}
                  onChange={(e) => patch({ currency: e.target.value })}>
                  {PLAN_CURRENCY_OPTIONS.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  required
                  label="Billing cycle"
                  value={form.billingInterval}
                  error={!!errors.billingInterval}
                  helperText={errors.billingInterval || 'Saved with the plan'}
                  onChange={(e) => {
                    patch({ billingInterval: e.target.value });
                    setErrors((prev) => {
                      if (!prev.billingInterval) return prev;
                      const nextErrors = { ...prev };
                      delete nextErrors.billingInterval;
                      return nextErrors;
                    });
                  }}
                >
                  {BILLING_INTERVAL_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  required
                  label="Trial days"
                  value={form.trialDays}
                  error={!!errors.trialDays}
                  helperText={
                    errors.trialDays
                    || `Min ${TENANT_PLAN_TRIAL_DAYS_MIN} · max ${TENANT_PLAN_TRIAL_DAYS_MAX}`
                  }
                  inputProps={{
                    min: TENANT_PLAN_TRIAL_DAYS_MIN,
                    max: TENANT_PLAN_TRIAL_DAYS_MAX,
                    step: 1,
                  }}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const next = raw === '' ? 0 : Number(raw);
                    const trialDays = Number.isFinite(next)
                      ? Math.min(Math.max(Math.trunc(next), 0), TENANT_PLAN_TRIAL_DAYS_MAX)
                      : 0;
                    patch({ trialDays });
                    setErrors((prev) => {
                      const nextErrors = { ...prev };
                      if (trialDays < TENANT_PLAN_TRIAL_DAYS_MIN) {
                        nextErrors.trialDays = `Trial days must be at least ${TENANT_PLAN_TRIAL_DAYS_MIN}`;
                      } else {
                        delete nextErrors.trialDays;
                      }
                      return nextErrors;
                    });
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Max users"
                  value={form.maxUsers ?? ''}
                  error={!!errors.maxUsers}
                  helperText={
                    errors.maxUsers
                    || `Blank = unlimited · max ${TENANT_PLAN_MAX_USERS_MAX.toLocaleString('en-IN')}`
                  }
                  inputProps={{
                    min: TENANT_PLAN_MAX_USERS_MIN,
                    max: TENANT_PLAN_MAX_USERS_MAX,
                    step: 1,
                  }}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      patch({ maxUsers: undefined });
                      setErrors((prev) => {
                        if (!prev.maxUsers) return prev;
                        const nextErrors = { ...prev };
                        delete nextErrors.maxUsers;
                        return nextErrors;
                      });
                      return;
                    }
                    const next = Number(raw);
                    const maxUsers = Number.isFinite(next)
                      ? Math.min(Math.max(Math.trunc(next), TENANT_PLAN_MAX_USERS_MIN), TENANT_PLAN_MAX_USERS_MAX)
                      : TENANT_PLAN_MAX_USERS_MIN;
                    patch({ maxUsers });
                    setErrors((prev) => {
                      if (!prev.maxUsers) return prev;
                      const nextErrors = { ...prev };
                      delete nextErrors.maxUsers;
                      return nextErrors;
                    });
                  }}
                />
              </Grid>
            </Grid>
          )}

          {activeTabId === 'modules' && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={4}
                  label="Plan highlights"
                  placeholder={'One benefit per line\ne.g. Email support\n3 service hubs'}
                  value={highlightsText}
                  error={!!errors.features}
                  helperText={errors.features || 'Shown as chips when selecting this plan on the tenant form'}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setHighlightsText(raw);
                    patch({ features: raw.split('\n').map((s) => s.trim()).filter(Boolean) });
                    const highlightsError = validatePlanHighlights(raw);
                    setErrors((prev) => {
                      if (highlightsError) return { ...prev, features: highlightsError };
                      if (!prev.features) return prev;
                      const next = { ...prev };
                      delete next.features;
                      return next;
                    });
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Switch checked={form.isActive} onChange={(e) => patch({ isActive: e.target.checked })} />}
                  label="Active — can be assigned to tenants"
                />
              </Grid>
            </Grid>
          )}
        </Box>

        <Divider />
        <Stack direction="row" justifyContent="space-between" sx={{ p: 2 }}>
          {tab > 0 ? (
            <Button onClick={() => { setTab((t) => t - 1); setErrors({}); }}>Previous</Button>
          ) : (
            <Box />
          )}
          <Stack direction="row" spacing={1}>
            <Button onClick={onClose}>Cancel</Button>
            {tab < TAB_IDS.length - 1 ? (
              <Button variant="contained" onClick={goNext}>Next</Button>
            ) : (
              <Button variant="contained" color="success" disabled={!canSave || loading} onClick={handleSave}>
                {loading ? 'Saving…' : mode === 'create' ? 'Create plan' : 'Save changes'}
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
}
